import React, { useEffect, useMemo, useRef, useState } from 'react';
import viewStyles from '../../assets/styles/LayoutSharedView.module.css';
import styles from './MensagensPage.module.css';
import api from '../../services/api';

type Contato = {
  uid_whatsapp_contato: string;
  wa_num_cliente: string;
  nome_exibicao?: string | null;
  nome_pescador?: string | null;
  ultima_interacao?: string | null;
  syn_cadmom?: string | null;
  interacao_humana?: boolean | number;
  // Bloqueio manual: a conversa segue no histórico, mas o sistema não responde
  // nem processa novas mensagens deste número até ser desbloqueado.
  bloqueado?: boolean;
  bloqueado_em?: string | null;
  // Janela de atendimento de 24h da Meta — só vem preenchido depois que a
  // conversa é aberta (GET /pesca/mensagens/contato/:uid), a lista de
  // contatos não traz esse campo.
  dentro_janela_24h?: boolean;
  janela_expira_em?: string | null;
};

type StatusMensagem = 'enviado' | 'entregue' | 'lido' | 'falhou';

type Mensagem = {
  uid_mensagem: string;
  uid_whatsapp_contato: string;
  direcao: 'in' | 'out';
  conteudo: string | null;
  message_type?: string | null;
  intent?: string | null;
  status?: StatusMensagem | null;
  payload_json?: any;
  syn_cadmom: string;
  // Foto vinculada (Epic 3, Task 3.6) — só vem preenchido quando message_type === 'image'
  tbl_pesca_fotos?: { uid_pesca_foto: string; url: string; legenda?: string | null }[];
};

type Template = {
  uid_whatsapp_template: string;
  uid_waba_numero: string;
  nome_meta: string;
  categoria: 'marketing' | 'utilidade' | 'autenticacao';
  idioma: string;
  texto_cabecalho?: string | null;
  texto_corpo: string;
  status_meta: 'em_analise' | 'aprovado' | 'rejeitado';
  ativo: boolean;
  // Presente pra qualquer template cadastrado com variáveis (Task 5) — só
  // usado aqui pra saber se esse template referencia "periodo" e por isso
  // vale a pena mostrar o seletor de mês de referência no envio.
  variaveis?: { chave: string; componente: 'cabecalho' | 'corpo'; parameter_name?: string | null }[];
};

const NOMES_MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const MensagensPage = () => {
  const chatRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pertoDoFinalRef = useRef(true);

  const [contatos, setContatos] = useState<Contato[]>([]);
  const [contatoSelecionado, setContatoSelecionado] = useState<Contato | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [loadingContatos, setLoadingContatos] = useState(true);
  const [loadingMensagens, setLoadingMensagens] = useState(false);
  const [textoEnvio, setTextoEnvio] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviandoRef = useRef(false); // trava imediata (não depende do ciclo de render do React)
  const [erroEnvio, setErroEnvio] = useState<string | null>(null);
  const [atualizandoInteracaoHumana, setAtualizandoInteracaoHumana] = useState(false);
  const [search, setSearch] = useState('');
  const [atualizandoBloqueio, setAtualizandoBloqueio] = useState(false);
  // Por padrão a lista esconde contatos bloqueados; este filtro mostra só eles.
  const [mostrarBloqueados, setMostrarBloqueados] = useState(false);
  // Navegação em duas etapas no mobile (Contatos -> Conversa -> Voltar) —
  // não tem efeito nenhum no desktop, onde os dois painéis sempre aparecem
  // lado a lado (ver MensagensPage.module.css).
  const [mostrarConversaMobile, setMostrarConversaMobile] = useState(false);

  // Indicador de data flutuante estilo WhatsApp — mostra o dia do trecho
  // visível da conversa enquanto rola, tanto pra cima (mensagens mais
  // antigas) quanto pra baixo (mais recentes). Os separadores de dia já
  // existentes (mudouDia, embutidos na lista) ficam como estão — este é um
  // indicador FLUTUANTE por cima, que atualiza sozinho conforme a rolagem
  // e some pouco depois de parar de rolar, pra não atrapalhar a leitura.
  // chave (YYYY-MM-DD) -> { el, dataRaw }, recriado do zero a cada render
  // na ordem cronológica em que os separadores aparecem na conversa.
  const separadoresRef = useRef<Map<string, { el: HTMLDivElement; dataRaw: string }>>(new Map());
  const [dataFlutuante, setDataFlutuante] = useState('');
  const [mostrarDataFlutuante, setMostrarDataFlutuante] = useState(false);
  const escondeDataFlutuanteTimeoutRef = useRef<any>(null);

  // Fora da janela de 24h, o input de texto livre dá lugar à lista de templates aprovados.
  // Dentro da janela, o envio de template continua disponível como opção extra —
  // ver mostrarPainelTemplate — porque às vezes é útil reabrir um assunto formal
  // (ex: aviso oficial) mesmo com a conversa livre ainda aberta.
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateSelecionadoUid, setTemplateSelecionadoUid] = useState('');
  const [enviandoTemplate, setEnviandoTemplate] = useState(false);
  const [mostrarPainelTemplate, setMostrarPainelTemplate] = useState(false);
  // Mês/ano de referência — só aparece quando o template escolhido usa a
  // variável "periodo" (ex: feedback_do_relatorio). Vira o contexto de mês
  // dessa conversa: se o pescador pedir o relatório em texto livre logo
  // depois sem dizer o mês, o backend usa o que foi enviado aqui em vez de
  // perguntar de novo (ver relatorioLivre.ts no inteagro-node).
  const [mesTemplateRef, setMesTemplateRef] = useState('');
  const [anoTemplateRef, setAnoTemplateRef] = useState('');

  const formatarTelefone = (telefone?: string | null) => {
    if (!telefone) return '-';

    const digits = telefone.replace(/\D/g, '');

    if (digits.length === 13) {
      return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
    }

    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }

    if (digits.length === 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    return telefone;
  };

  const formatarDataHora = (dataRaw?: string | null) => {
    if (!dataRaw) return '-';

    const d = new Date(dataRaw);
    if (Number.isNaN(d.getTime())) return '-';

    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatarHora = (dataRaw?: string | null) => {
    if (!dataRaw) return '';

    const d = new Date(dataRaw);
    if (Number.isNaN(d.getTime())) return '';

    return d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChaveData = (dataRaw?: string | null) => {
    if (!dataRaw) return '';
    const d = new Date(dataRaw);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const formatarDataSeparador = (dataRaw?: string | null) => {
    if (!dataRaw) return '';
    const d = new Date(dataRaw);
    if (Number.isNaN(d.getTime())) return '';

    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const obterTextoMensagem = (mensagem: Mensagem) => {
    if (mensagem.conteudo && mensagem.conteudo.trim()) return mensagem.conteudo;
    if (mensagem.message_type && mensagem.message_type !== 'text') return `[${mensagem.message_type}]`;
    return '[mensagem sem conteúdo]';
  };

  const getTipoMensagemLabel = (msg: Mensagem) => {
    const tipo = (msg.message_type || '').toLowerCase();

    if (tipo === 'audio' || tipo === 'voice') return '🎤 Áudio';
    if (tipo === 'text') return '📝 Texto';
    if (tipo) return `📎 ${tipo}`;
    return '📝 Texto';
  };

  // "Tracinhos" de status estilo WhatsApp — só faz sentido pra mensagens
  // enviadas por nós ('out'). O status vem do backend, atualizado conforme
  // os callbacks de status da Meta chegam no webhook (delivered/read/failed).
  const renderStatusMensagem = (msg: Mensagem) => {
    if (msg.direcao !== 'out') return null;

    const status = msg.status || 'enviado';

    if (status === 'falhou') {
      const erro = msg.payload_json?.status_errors?.[0];
      const titulo = erro
        ? `Falha no envio: ${erro.message || erro.title || 'erro desconhecido'} (código ${erro.code ?? '?'})`
        : 'Falha no envio';
      return (
        <span title={titulo} style={{ color: '#f75a68', fontWeight: 700, fontSize: '0.75rem' }}>
          ⚠
        </span>
      );
    }

    if (status === 'lido') {
      return (
        <span title="Lido" style={{ color: '#53bdeb', fontSize: '0.72rem', letterSpacing: '-2px' }}>
          ✓✓
        </span>
      );
    }

    if (status === 'entregue') {
      return (
        <span title="Entregue" style={{ color: '#667781', fontSize: '0.72rem', letterSpacing: '-2px' }}>
          ✓✓
        </span>
      );
    }

    // enviado (default)
    return (
      <span title="Enviado" style={{ color: '#667781', fontSize: '0.72rem' }}>
        ✓
      </span>
    );
  };

  const carregarContatos = async (silencioso = false) => {
    try {
      if (!silencioso) setLoadingContatos(true);

      const res = await api.get('/pesca/mensagens/contatos', {
        params: { search, bloqueados: mostrarBloqueados ? 'true' : undefined }
      });

      const lista = res.data?.data || [];
      setContatos(lista);

      if (!contatoSelecionado && lista.length > 0) {
        setContatoSelecionado(lista[0]);
      } else if (contatoSelecionado) {
        const atualizado = lista.find(
          (c: Contato) => c.uid_whatsapp_contato === contatoSelecionado.uid_whatsapp_contato
        );
        if (atualizado) {
          setContatoSelecionado(atualizado);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar contatos:', error);
      if (!silencioso) setContatos([]);
    } finally {
      if (!silencioso) setLoadingContatos(false);
    }
  };

  const carregarMensagens = async (uidContato: string, silencioso = false) => {
    try {
      if (!silencioso) setLoadingMensagens(true);

      const res = await api.get(`/pesca/mensagens/contato/${uidContato}`);
      setMensagens(res.data?.data?.mensagens || []);

      // A lista de contatos (carregarContatos) não traz dentro_janela_24h —
      // só o histórico de uma conversa aberta traz. Mescla esses dois campos
      // no contato selecionado, sem sobrescrever o resto do estado dele.
      const contatoResp = res.data?.data?.contato;
      if (contatoResp) {
        setContatoSelecionado(prev =>
          prev && prev.uid_whatsapp_contato === uidContato
            ? { ...prev, dentro_janela_24h: contatoResp.dentro_janela_24h, janela_expira_em: contatoResp.janela_expira_em }
            : prev
        );
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      if (!silencioso) setMensagens([]);
    } finally {
      if (!silencioso) setLoadingMensagens(false);
    }
  };

  const handleEnviarMensagem = async () => {
    if (!contatoSelecionado || !textoEnvio.trim() || enviandoRef.current || contatoSelecionado.dentro_janela_24h === false || contatoSelecionado.bloqueado) return;
    enviandoRef.current = true;
    setEnviando(true);
    setErroEnvio(null);

    const textoParaEnviar = textoEnvio.trim();
    // Limpa o campo já na hora, pra ficar visualmente óbvio que foi enviado
    // e evitar reenvio acidental do mesmo texto.
    setTextoEnvio('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    try {
      await api.post(`/pesca/mensagens/contato/${contatoSelecionado.uid_whatsapp_contato}/enviar`, {
        texto: textoParaEnviar,
      });
      pertoDoFinalRef.current = true;
      await carregarMensagens(contatoSelecionado.uid_whatsapp_contato, true);
    } catch (error: any) {
      console.error('Erro ao enviar mensagem:', error);
      // Se falhou de verdade, devolve o texto pro campo pra não perder o que foi digitado
      setTextoEnvio(textoParaEnviar);
      setErroEnvio(error?.response?.data?.message || 'Erro ao enviar mensagem.');
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  };

  const carregarTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const res = await api.get('/pesca/templates');
      const lista: Template[] = res.data?.data || [];
      // Só templates ativos e já aprovados pela Meta podem ser enviados de verdade.
      setTemplates(lista.filter(t => t.ativo && t.status_meta === 'aprovado'));
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const templateSelecionado = templates.find((t) => t.uid_whatsapp_template === templateSelecionadoUid);
  const templateUsaPeriodo = !!templateSelecionado?.variaveis?.some((v) => v.chave === 'periodo');

  const handleEnviarTemplate = async () => {
    if (!contatoSelecionado || !templateSelecionadoUid || enviandoTemplate) return;
    setEnviandoTemplate(true);
    setErroEnvio(null);
    try {
      // Nenhum campo de variável é enviado — o backend resolve tudo (nome do
      // pescador, período, etc.) automaticamente a partir do catálogo fixo.
      // mes/ano são a ÚNICA exceção: um override opcional do "mês atual"
      // padrão da variável periodo, só quando o template usa essa variável.
      await api.post(`/pesca/mensagens/contato/${contatoSelecionado.uid_whatsapp_contato}/enviar-template`, {
        uid_whatsapp_template: templateSelecionadoUid,
        ...(templateUsaPeriodo && mesTemplateRef && anoTemplateRef
          ? { mes: Number(mesTemplateRef), ano: Number(anoTemplateRef) }
          : {}),
      });
      setTemplateSelecionadoUid('');
      setMesTemplateRef('');
      setAnoTemplateRef('');
      pertoDoFinalRef.current = true;
      await carregarMensagens(contatoSelecionado.uid_whatsapp_contato);
    } catch (error: any) {
      console.error('Erro ao enviar template:', error);
      setErroEnvio(error?.response?.data?.message || 'Erro ao enviar template.');
    } finally {
      setEnviandoTemplate(false);
    }
  };

  const toggleInteracaoHumana = async () => {
    if (!contatoSelecionado || atualizandoInteracaoHumana) return;
    const novoValor = !contatoSelecionado.interacao_humana;
    setAtualizandoInteracaoHumana(true);
    try {
      await api.put(`/pesca/mensagens/contato/${contatoSelecionado.uid_whatsapp_contato}/interacao-humana`, {
        ativo: novoValor,
      });
      setContatoSelecionado(prev => prev ? { ...prev, interacao_humana: novoValor } : prev);
      setContatos(prev => prev.map(c =>
        c.uid_whatsapp_contato === contatoSelecionado.uid_whatsapp_contato
          ? { ...c, interacao_humana: novoValor }
          : c
      ));
    } catch (error) {
      console.error('Erro ao atualizar interação humana:', error);
    } finally {
      setAtualizandoInteracaoHumana(false);
    }
  };

  const toggleBloqueio = async () => {
    if (!contatoSelecionado || atualizandoBloqueio) return;
    const novoValor = !contatoSelecionado.bloqueado;
    if (novoValor && !window.confirm('Bloquear este contato? O sistema deixará de responder e processar novas mensagens deste número. O contato poderá ser desbloqueado posteriormente.')) {
      return;
    }
    setAtualizandoBloqueio(true);
    setErroEnvio(null);
    try {
      const res = await api.put(`/pesca/mensagens/contato/${contatoSelecionado.uid_whatsapp_contato}/bloqueio`, {
        bloqueado: novoValor,
      });
      const bloqueadoEm = res.data?.data?.bloqueado_em ?? null;
      setContatoSelecionado(prev => prev ? { ...prev, bloqueado: novoValor, bloqueado_em: bloqueadoEm } : prev);
      setContatos(prev => prev.map(c =>
        c.uid_whatsapp_contato === contatoSelecionado.uid_whatsapp_contato
          ? { ...c, bloqueado: novoValor, bloqueado_em: bloqueadoEm }
          : c
      ));
      // Contato bloqueado sai da lista padrão (e contato desbloqueado sai do filtro de bloqueados).
      await carregarContatos(true);
    } catch (error: any) {
      console.error('Erro ao atualizar bloqueio:', error);
      setErroEnvio(error?.response?.data?.message || 'Erro ao atualizar o bloqueio do contato.');
    } finally {
      setAtualizandoBloqueio(false);
    }
  };

  useEffect(() => {
    carregarContatos();
  }, [search, mostrarBloqueados]);

  useEffect(() => {
    if (contatoSelecionado?.uid_whatsapp_contato) {
      carregarMensagens(contatoSelecionado.uid_whatsapp_contato);
    } else {
      setMensagens([]);
    }
  }, [contatoSelecionado?.uid_whatsapp_contato]);

  // Polling: atualiza a conversa aberta a cada 5s, sem precisar sair e voltar da tela
  useEffect(() => {
    if (!contatoSelecionado?.uid_whatsapp_contato) return;
    const uidAtual = contatoSelecionado.uid_whatsapp_contato;
    const intervalo = setInterval(() => {
      carregarMensagens(uidAtual, true);
    }, 5000);
    return () => clearInterval(intervalo);
  }, [contatoSelecionado?.uid_whatsapp_contato]);

  // Polling: atualiza a lista de contatos (ordem/última interação) a cada 15s
  useEffect(() => {
    const intervalo = setInterval(() => {
      carregarContatos(true);
    }, 15000);
    return () => clearInterval(intervalo);
  }, [search, contatoSelecionado]);

  useEffect(() => () => {
    if (escondeDataFlutuanteTimeoutRef.current) clearTimeout(escondeDataFlutuanteTimeoutRef.current);
  }, []);

  // Ao trocar de contato, sempre volta pro final da conversa e limpa erro de envio anterior
  useEffect(() => {
    pertoDoFinalRef.current = true;
    setErroEnvio(null);
    setTemplateSelecionadoUid('');
    setMesTemplateRef('');
    setAnoTemplateRef('');
    setMostrarPainelTemplate(false);
  }, [contatoSelecionado?.uid_whatsapp_contato]);

  // Busca templates sempre que um contato é selecionado — o envio de
  // template fica disponível independente do estado da janela de 24h
  // (fora da janela é a única opção; dentro da janela é uma opção extra).
  useEffect(() => {
    if (contatoSelecionado?.uid_whatsapp_contato) {
      carregarTemplates();
    }
  }, [contatoSelecionado?.uid_whatsapp_contato]);

  // Acha o separador de dia mais próximo do topo (já passou ou está bem
  // perto dele) — é essa data que fica pinada no indicador flutuante,
  // igual o WhatsApp: mostra o dia do trecho que está visível no topo da
  // tela, atualizando conforme cruza pra outro dia em qualquer direção.
  const atualizarDataFlutuante = () => {
    const container = chatRef.current;
    if (!container || separadoresRef.current.size === 0) return;

    const topoContainer = container.getBoundingClientRect().top;
    let dataRawAtual: string | null = null;
    for (const { el, dataRaw } of separadoresRef.current.values()) {
      const topoRelativo = el.getBoundingClientRect().top - topoContainer;
      if (topoRelativo <= 48) {
        dataRawAtual = dataRaw;
      } else {
        break;
      }
    }
    // Ainda não rolou até passar do primeiro separador — mostra o dia
    // dele mesmo assim (é o que já está visível no topo da conversa).
    if (!dataRawAtual) {
      dataRawAtual = separadoresRef.current.values().next().value?.dataRaw ?? null;
    }
    if (dataRawAtual) setDataFlutuante(formatarDataSeparador(dataRawAtual));
  };

  const handleScrollChat = () => {
    if (!chatRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
    pertoDoFinalRef.current = scrollHeight - scrollTop - clientHeight < 80;

    atualizarDataFlutuante();
    setMostrarDataFlutuante(true);
    if (escondeDataFlutuanteTimeoutRef.current) clearTimeout(escondeDataFlutuanteTimeoutRef.current);
    // Some pouco depois de parar de rolar — só aparece durante a
    // navegação, pra não competir com a leitura das mensagens.
    escondeDataFlutuanteTimeoutRef.current = setTimeout(() => setMostrarDataFlutuante(false), 1200);
  };

  useEffect(() => {
    if (chatRef.current && pertoDoFinalRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [mensagens, contatoSelecionado]);

  const getNomeContato = (contato?: Contato | null) => {
    return contato?.nome_pescador || contato?.nome_exibicao || 'Usuário não identificado';
  };

  const tituloContato = useMemo(() => {
    return getNomeContato(contatoSelecionado);
  }, [contatoSelecionado]);

  const renderPainelTemplate = () => (
    loadingTemplates ? (
      <div style={{ color: '#666', fontSize: '0.85rem' }}>Carregando templates...</div>
    ) : templates.length === 0 ? (
      <div style={{ color: '#666', fontSize: '0.85rem' }}>
        Nenhum template aprovado disponível. Cadastre um em Templates.
      </div>
    ) : (
      <>
        <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
          {templates.map((template) => {
            const selecionado = templateSelecionadoUid === template.uid_whatsapp_template;
            return (
              <button
                key={template.uid_whatsapp_template}
                onClick={() => {
                  setTemplateSelecionadoUid(template.uid_whatsapp_template);
                  // Troca de template limpa o mês escolhido — não faz
                  // sentido carregar o mês de um template pro outro.
                  setMesTemplateRef('');
                  setAnoTemplateRef(String(new Date().getFullYear()));
                }}
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  borderRadius: '10px',
                  border: selecionado ? '2px solid #03c9fe' : '1px solid #ddd',
                  background: selecionado ? '#eaf9ff' : '#fafafa',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#222' }}>
                  {template.nome_meta}
                </div>
                <div
                  style={{
                    fontSize: '0.78rem',
                    color: '#777',
                    marginTop: '2px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {template.texto_corpo}
                </div>
              </button>
            );
          })}
        </div>

        {templateUsaPeriodo && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.8rem', color: '#666', flexShrink: 0 }}>Mês de referência:</span>
            <select
              value={mesTemplateRef}
              onChange={(e) => setMesTemplateRef(e.target.value)}
              style={{ flex: 1, padding: '7px 10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.82rem' }}
            >
              <option value="">Mês atual (padrão)</option>
              {NOMES_MESES.map((nome, idx) => (
                <option key={nome} value={idx + 1}>{nome}</option>
              ))}
            </select>
            {mesTemplateRef && (
              <input
                type="number"
                value={anoTemplateRef}
                onChange={(e) => setAnoTemplateRef(e.target.value)}
                style={{ width: '80px', padding: '7px 8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '0.82rem' }}
              />
            )}
          </div>
        )}

        <button
          onClick={async () => {
            await handleEnviarTemplate();
            setMostrarPainelTemplate(false);
          }}
          disabled={!templateSelecionadoUid || enviandoTemplate}
          style={{
            width: '100%',
            padding: '10px 20px',
            borderRadius: '20px',
            border: 'none',
            background: '#03c9fe',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
            opacity: (!templateSelecionadoUid || enviandoTemplate) ? 0.6 : 1,
          }}
        >
          {enviandoTemplate ? 'Enviando...' : 'Enviar template'}
        </button>
      </>
    )
  );

  // Limpa e reconstrói o mapa de separadores de dia a cada render, na mesma
  // ordem em que aparecem na conversa — atualizarDataFlutuante lê daqui
  // pra saber qual dia mostrar no indicador flutuante durante a rolagem.
  separadoresRef.current.clear();

  return (
    <div className={`${viewStyles.container} ${styles.pageWrap}`}>
      <div className={viewStyles.headerTitle}>
        <h1 className={`${viewStyles.greenTitle} ${styles.tituloMobile}`}>Mensagens</h1>
      </div>

      <div className={`${styles.grid} ${mostrarConversaMobile ? styles.mostrandoConversa : styles.mostrandoContatos}`}>
        <div className={`${viewStyles.sectionCard} ${styles.painelContatos}`}>
          <div className={viewStyles.sectionHeader}>CONTATOS</div>

          <div style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
            <input
              type="text"
              placeholder="Buscar por nome ou telefone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                outline: 'none',
                fontSize: '0.95rem'
              }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.8rem', color: '#666', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={mostrarBloqueados}
                onChange={(e) => setMostrarBloqueados(e.target.checked)}
              />
              Mostrar somente bloqueados
            </label>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {loadingContatos ? (
              <div style={{ padding: '16px' }}>Carregando contatos...</div>
            ) : contatos.length === 0 ? (
              <div style={{ padding: '16px', color: '#666' }}>Nenhum contato encontrado.</div>
            ) : (
              contatos.map((contato) => {
                const ativo =
                  contatoSelecionado?.uid_whatsapp_contato === contato.uid_whatsapp_contato;

                return (
                  <button
                    key={contato.uid_whatsapp_contato}
                    onClick={() => { setContatoSelecionado(contato); setMostrarConversaMobile(true); }}
                    style={{
                      width: '100%',
                      border: 'none',
                      background: ativo ? '#eef6ff' : '#fff',
                      borderBottom: '1px solid #f0f0f0',
                      padding: '14px 16px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px'
                    }}
                  >
                    <strong style={{ fontSize: '0.95rem', color: '#222' }}>
                      {getNomeContato(contato)}
                      {contato.interacao_humana && (
                        <span style={{
                          marginLeft: '8px', fontSize: '0.68rem', fontWeight: 700, color: '#856404',
                          background: '#fff3cd', padding: '2px 8px', borderRadius: '10px',
                        }}>
                          HUMANO
                        </span>
                      )}
                      {contato.bloqueado && (
                        <span style={{
                          marginLeft: '8px', fontSize: '0.68rem', fontWeight: 700, color: '#721c24',
                          background: '#f8d7da', padding: '2px 8px', borderRadius: '10px',
                        }}>
                          BLOQUEADO
                        </span>
                      )}
                    </strong>

                    {contato.nome_pescador && contato.nome_exibicao && contato.nome_pescador !== contato.nome_exibicao && (
                      <span style={{ fontSize: '0.78rem', color: '#999' }}>
                        {contato.nome_exibicao}
                      </span>
                    )}

                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                      {formatarTelefone(contato.wa_num_cliente)}
                    </span>

                    <span style={{ fontSize: '0.78rem', color: '#999' }}>
                      Última interação: {formatarDataHora(contato.ultima_interacao)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className={`${viewStyles.sectionCard} ${styles.painelConversa}`}>
          <div className={`${viewStyles.sectionHeader} ${styles.cabecalhoConversa}`}>
            <button
              type="button"
              className={styles.botaoVoltar}
              onClick={() => setMostrarConversaMobile(false)}
              aria-label="Voltar para a lista de contatos"
            >
              ‹ Contatos
            </button>
            <span className={styles.tituloConversaDesktop}>CONVERSA</span>
            <div className={styles.cabecalhoConversaInfo}>
              <span className={styles.nomeContatoCabecalho} style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                {tituloContato}
                {contatoSelecionado?.wa_num_cliente
                  ? ` • ${formatarTelefone(contatoSelecionado.wa_num_cliente)}`
                  : ''}
              </span>
              {contatoSelecionado && (
                <button
                  onClick={toggleBloqueio}
                  disabled={atualizandoBloqueio}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    border: contatoSelecionado.bloqueado ? '1px solid #721c24' : 'none',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    background: contatoSelecionado.bloqueado ? '#fff' : '#6c757d',
                    color: contatoSelecionado.bloqueado ? '#721c24' : '#fff',
                    flexShrink: 0,
                  }}
                >
                  {contatoSelecionado.bloqueado ? 'Desbloquear contato' : 'Bloquear contato'}
                </button>
              )}
              {contatoSelecionado && (
                <button
                  onClick={toggleInteracaoHumana}
                  disabled={atualizandoInteracaoHumana}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    background: contatoSelecionado.interacao_humana ? '#f75a68' : '#00b37e',
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  {contatoSelecionado.interacao_humana ? 'Devolver pra IA' : 'Assumir conversa'}
                </button>
              )}
            </div>
          </div>

          {contatoSelecionado?.bloqueado && (
            <div style={{ background: '#f8d7da', color: '#721c24', padding: '8px 16px', fontSize: '0.8rem', textAlign: 'center' }}>
              Contato bloqueado — o sistema não responde nem processa novas mensagens deste número. O histórico foi preservado.
            </div>
          )}

          {contatoSelecionado?.interacao_humana && (
            <div style={{ background: '#fff3cd', color: '#856404', padding: '8px 16px', fontSize: '0.8rem', textAlign: 'center' }}>
              Conversa assumida por um humano — os agentes de IA não vão responder até você devolver.
            </div>
          )}

          {contatoSelecionado && contatoSelecionado.dentro_janela_24h === false && (
            <div style={{ background: '#f8d7da', color: '#721c24', padding: '8px 16px', fontSize: '0.8rem', textAlign: 'center' }}>
              ⏰ Fora da janela de 24h do WhatsApp — não é possível mandar texto livre. Envie um template pré-aprovado para reabrir a conversa.
            </div>
          )}

          <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
            {mensagens.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '10px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 2,
                  background: '#fff',
                  color: '#54656f',
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  padding: '6px 14px',
                  borderRadius: '999px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  opacity: mostrarDataFlutuante ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                }}
              >
                {dataFlutuante}
              </div>
            )}
            <div
              ref={chatRef}
              onScroll={handleScrollChat}
              style={{
                position: 'absolute',
                inset: 0,
                overflowY: 'auto',
                padding: '20px',
                background: '#efeae2',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}
            >
            {!contatoSelecionado ? (
              <div
                style={{
                  margin: 'auto',
                  background: '#fff',
                  padding: '14px 18px',
                  borderRadius: '8px',
                  color: '#666'
                }}
              >
                Selecione um contato.
              </div>
            ) : loadingMensagens ? (
              <div
                style={{
                  margin: 'auto',
                  background: '#fff',
                  padding: '14px 18px',
                  borderRadius: '8px',
                  color: '#666'
                }}
              >
                Carregando conversa...
              </div>
            ) : mensagens.length === 0 ? (
              <div
                style={{
                  margin: 'auto',
                  background: '#fff',
                  padding: '14px 18px',
                  borderRadius: '8px',
                  color: '#666'
                }}
              >
                Nenhuma mensagem encontrada para este contato.
              </div>
            ) : (
              mensagens.map((msg, index) => {
                const inbound = msg.direcao === 'in';
                const mensagemAnterior = index > 0 ? mensagens[index - 1] : null;
                const mudouDia =
                  !mensagemAnterior ||
                  getChaveData(mensagemAnterior.syn_cadmom) !== getChaveData(msg.syn_cadmom);
                const chaveDia = getChaveData(msg.syn_cadmom);

                return (
                  <React.Fragment key={msg.uid_mensagem}>
                    {mudouDia && (
                      <div
                        ref={(el) => {
                          if (el) separadoresRef.current.set(chaveDia, { el, dataRaw: msg.syn_cadmom });
                        }}
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          margin: '8px 0 2px'
                        }}
                      >
                        <div
                          style={{
                            background: '#fff',
                            color: '#666',
                            fontSize: '0.78rem',
                            padding: '6px 12px',
                            borderRadius: '999px',
                            boxShadow: '0 1px 1px rgba(0,0,0,0.08)'
                          }}
                        >
                          {formatarDataSeparador(msg.syn_cadmom)}
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: inbound ? 'flex-start' : 'flex-end'
                      }}
                    >
                      <div
                        className={styles.bolhaMensagem}
                        style={{
                          background: inbound ? '#ffffff' : '#d9fdd3',
                          borderRadius: '8px',
                          padding: '10px 12px 18px 12px',
                          position: 'relative',
                          boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontSize: '0.95rem',
                          lineHeight: 1.45
                        }}
                      >
                        <div
                          style={{
                            fontSize: '0.72rem',
                            color: inbound ? '#667781' : '#4f6f52',
                            fontWeight: 600,
                            marginBottom: '4px'
                          }}
                        >
                          {inbound ? getNomeContato(contatoSelecionado) : 'Inteagro'}
                        </div>

                        <div
                          style={{
                            fontSize: '0.7rem',
                            color: '#888',
                            fontWeight: 500,
                            marginBottom: '6px'
                          }}
                        >
                          {getTipoMensagemLabel(msg)}
                        </div>

                        {msg.message_type === 'image' && msg.tbl_pesca_fotos && msg.tbl_pesca_fotos.length > 0 ? (
                          <div>
                            <img
                              src={msg.tbl_pesca_fotos[0].url}
                              alt={msg.tbl_pesca_fotos[0].legenda || 'Foto enviada pelo pescador'}
                              style={{ maxWidth: '100%', maxHeight: '260px', borderRadius: '6px', display: 'block', objectFit: 'cover' }}
                            />
                            {msg.tbl_pesca_fotos[0].legenda && (
                              <div style={{ marginTop: '6px' }}>{msg.tbl_pesca_fotos[0].legenda}</div>
                            )}
                          </div>
                        ) : (
                          <div>{obterTextoMensagem(msg)}</div>
                        )}

                        <div
                          style={{
                            position: 'absolute',
                            right: '10px',
                            bottom: '5px',
                            fontSize: '0.68rem',
                            color: '#667781',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          {formatarHora(msg.syn_cadmom)}
                          {renderStatusMensagem(msg)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              }))}
            </div>
          </div>

          {contatoSelecionado && (
            <>
              {erroEnvio && (
                <div style={{ padding: '6px 16px 0', color: '#f75a68', fontSize: '0.78rem', textAlign: 'center' }}>
                  {erroEnvio}
                </div>
              )}

              {contatoSelecionado.bloqueado ? (
                <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', background: '#f8f9fa', color: '#721c24', fontSize: '0.85rem', textAlign: 'center' }}>
                  Envio desabilitado para contato bloqueado. Desbloqueie para voltar a enviar mensagens.
                </div>
              ) : contatoSelecionado.dentro_janela_24h === false ? (
                <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', background: '#fff' }}>
                  {renderPainelTemplate()}
                </div>
              ) : (
                <>
                  {/* Envio de template continua disponível mesmo com a janela de 24h
                      aberta — antes só aparecia quando a janela estava fechada, o
                      que tornava impossível disparar um template a qualquer momento. */}
                  {mostrarPainelTemplate && (
                    <div style={{ padding: '12px 16px', borderTop: '1px solid #eee', background: '#fafeff' }}>
                      {renderPainelTemplate()}
                    </div>
                  )}
                  <div className={styles.composer} style={{ display: 'flex', gap: '10px', padding: '12px 16px', borderTop: mostrarPainelTemplate ? 'none' : '1px solid #eee', background: '#fff', alignItems: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setMostrarPainelTemplate(v => !v)}
                      title={mostrarPainelTemplate ? 'Fechar templates' : 'Enviar template'}
                      style={{
                        padding: '10px 12px',
                        borderRadius: '18px',
                        border: mostrarPainelTemplate ? '2px solid #03c9fe' : '1px solid #ddd',
                        background: mostrarPainelTemplate ? '#eaf9ff' : '#fafafa',
                        color: '#333',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      📋 Template
                    </button>
                    <textarea
                      ref={textareaRef}
                      value={textoEnvio}
                      onChange={(e) => {
                        setTextoEnvio(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = `${Math.min(e.target.scrollHeight, 140)}px`;
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEnviarMensagem(); } }}
                      placeholder={
                        contatoSelecionado.interacao_humana
                          ? 'Digite sua mensagem... (Enter envia, Shift+Enter pula linha)'
                          : 'Assuma a conversa antes de enviar (recomendado)'
                      }
                      rows={1}
                      style={{
                        flex: 1, padding: '10px 14px', borderRadius: '18px', border: '1px solid #ddd', outline: 'none',
                        fontSize: '0.9rem', fontFamily: 'inherit', resize: 'none', overflowY: 'auto', maxHeight: '140px', lineHeight: 1.4,
                      }}
                    />
                    <button
                      onClick={handleEnviarMensagem}
                      disabled={enviando || !textoEnvio.trim()}
                      style={{
                        padding: '10px 20px', borderRadius: '20px', border: 'none', background: '#03c9fe', color: '#fff',
                        cursor: 'pointer', fontWeight: 600,
                        opacity: (enviando || !textoEnvio.trim()) ? 0.6 : 1,
                      }}
                    >
                      {enviando ? 'Enviando...' : 'Enviar'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MensagensPage;